import { useState } from 'react'
import { ThumbsUpIcon, ThumbsDownIcon, NotePencilIcon } from '@phosphor-icons/react'
import { ScoopText, ScoopDivider, ScoopColumn } from '../../../../../../ui'
import { useModal, MODALS } from '../../../../../../modals'
import { EVENTS, trackEvent } from '../../../../../../../events'
import styles from './FeedbackSection.module.scss'

export const FeedbackSection = ({
    correlationID,
    messageID,
    hasFooter,
    hasContent,
    submitFeedback,
    isSubmittingFeedback,
}) => {
    const [feedbackSubmitted, setFeedbackSubmitted] = useState(false)
    const [feedbackType, setFeedbackType] = useState(null)
    const feedbackModal = useModal(MODALS.CHAT.FEEDBACK)

    // Don't render if no correlationID or no mutation function
    if (!correlationID || !submitFeedback) return null

    // Feedback handlers
    const handleFeedback = (type) => {
        // Submit feedback using mutation
        submitFeedback(
            { correlationID, feedbackType: type, userCorrection: null },
            {
                onSuccess: () => {
                    // Track analytics
                    trackEvent(
                        type === 'POSITIVE'
                            ? EVENTS.CHAT_FEEDBACK.POSITIVE
                            : EVENTS.CHAT_FEEDBACK.NEGATIVE,
                        {
                            correlationID,
                            messageID,
                            feedbackType: type,
                        }
                    )

                    // Update UI state
                    setFeedbackType(type)
                    setFeedbackSubmitted(true)
                },
                onError: (error) => {
                    console.error('Error submitting feedback:', error)
                },
            }
        )
    }

    const handleOpenFeedbackModal = async () => {
        const result = await feedbackModal.show({ isSubmitting: isSubmittingFeedback })
        if (result?.value) {
            submitFeedback(
                { correlationID, feedbackType: 'CORRECTION', userCorrection: result.value },
                {
                    onSuccess: () => {
                        // Track analytics
                        trackEvent(EVENTS.CHAT_FEEDBACK.CORRECTION, {
                            correlationID,
                            messageID,
                            feedbackType: 'CORRECTION',
                            hasCorrectionText: !!result.value,
                        })
                        setFeedbackType('CORRECTION')
                        setFeedbackSubmitted(true)
                    },
                    onError: (error) => {
                        console.error('Error submitting correction:', error)
                    },
                }
            )
        }
    }

    // Determine thank you message based on feedback type
    const getThankYouMessage = () => {
        if (feedbackType === 'POSITIVE') {
            return 'Thanks! This helps us learn your data'
        } else if (feedbackType === 'NEGATIVE') {
            return 'Thanks for letting us know'
        } else if (feedbackType === 'CORRECTION') {
            return "Thank you! We'll use this to improve our understanding"
        }
        return 'Thank you for your feedback!'
    }

    return (
        <>
            {/* Divider before feedback */}
            {(hasFooter || hasContent) && <ScoopDivider className={styles.divider} />}

            <ScoopColumn className={styles.container}>
                {!feedbackSubmitted ? (
                    <>
                        <ScoopText className={styles.title}>Was this helpful?</ScoopText>
                        <ScoopColumn className={styles.optionsContainer}>
                            <div
                                onClick={() => handleFeedback('POSITIVE')}
                                className={styles.optionItem}
                            >
                                <ThumbsUpIcon size={14} weight="regular" />
                                <ScoopText className={styles.optionText}>
                                    Yes, this is correct
                                </ScoopText>
                            </div>
                            <div
                                onClick={() => handleFeedback('NEGATIVE')}
                                className={styles.optionItem}
                            >
                                <ThumbsDownIcon size={14} weight="regular" />
                                <ScoopText className={styles.optionText}>
                                    No, something's wrong
                                </ScoopText>
                            </div>
                            <div onClick={handleOpenFeedbackModal} className={styles.optionItem}>
                                <NotePencilIcon size={14} weight="regular" />
                                <ScoopText className={styles.optionText}>
                                    Tell us what you expected
                                </ScoopText>
                            </div>
                        </ScoopColumn>
                    </>
                ) : (
                    <div className={styles.thankYouContainer}>
                        <ScoopText className={styles.thankYouText}>
                            {getThankYouMessage()}
                        </ScoopText>
                    </div>
                )}
            </ScoopColumn>
        </>
    )
}
